import mapboxgl, { Layer, Map } from 'mapbox-gl';

import { HIGHLIGHT_LAYERS } from '../mapData/highlightLayers';
import { LayerWithToggleableGroup } from '../types/types';

const clearActiveHighlight = (map: Map, highlightLayer: keyof typeof HIGHLIGHT_LAYERS): void => {
  const { filterProperty, sourceLayers, highlightLayers, getIconSizes } =
    HIGHLIGHT_LAYERS[highlightLayer];

  // reset icon sizes in source map layers
  sourceLayers.forEach(({ id }) => {
    if (map.getLayer(id)) {
      const { normalIconSize } = getIconSizes(id);
      map.setLayoutProperty(id, 'icon-size', normalIconSize);
    }
  });

  // remove highlights in highlight layers
  highlightLayers.forEach(({ id }) => {
    if (map.getLayer(id)) {
      map.setFilter(id, ['==', filterProperty, '']);
    }
  });
};

export const clearActiveHighlights = (map: Map): void => {
  Object.keys(HIGHLIGHT_LAYERS).forEach(highlightLayer => {
    clearActiveHighlight(map, highlightLayer as keyof typeof HIGHLIGHT_LAYERS);
  });
};

export const checkAndReturnCatchIconData = (
  features: mapboxgl.MapboxGeoJSONFeature[],
  longitude: any,
  latitude: any,
): string => {
  const {
    // @ts-ignore
    properties: { external_id: dataPointId },
  } = features[0];

  if (!dataPointId) {
    throw new Error(
      `No externalId found when clicking the icon: Id: ${dataPointId}, Lat: ${longitude}, Lng: ${latitude}`,
    );
  }

  if (typeof dataPointId !== 'string') {
    throw new Error(`The provided externalId was not a string. Id: ${dataPointId}`);
  }

  return dataPointId;
};

export const checkAndReturnPointsOfInterestIconData = (
  features: mapboxgl.MapboxGeoJSONFeature[],
  longitude: any,
  latitude: any,
): number => {
  const {
    // @ts-ignore
    properties: { id },
  } = features[0];
  if (!id) {
    throw new Error(
      `No Id found when clicking the point of interest: Id: ${id}, Lat: ${longitude}, Lng: ${latitude}`,
    );
  }
  if (typeof id !== 'number') {
    throw new Error(`The provided externalId was not a number. Id: ${id}`);
  }

  return id;
};

export const checkAndReturnArtificialReefIconData = (
  features: mapboxgl.MapboxGeoJSONFeature[],
  longitude: any,
  latitude: any,
): string => {
  const {
    // @ts-ignore
    properties: { id },
  } = features[0];
  if (!id) {
    throw new Error(
      `No Id found when clicking the point of interest: Id: ${id}, Lat: ${longitude}, Lng: ${latitude}`,
    );
  }
  if (typeof id !== 'string') {
    throw new Error(`The provided externalId was not a number. Id: ${id}`);
  }

  return id;
};

export const checkAndReturnWaypointIconData = (
  features: mapboxgl.MapboxGeoJSONFeature[],
  longitude: any,
  latitude: any,
): string => {
  const {
    // @ts-ignore
    properties: { external_id: dataPointId, id },
  } = features[0];
  if (!dataPointId) {
    throw new Error(
      `No Id found when clicking the point of interest: Id: ${id}, Lat: ${longitude}, Lng: ${latitude}`,
    );
  }
  if (typeof dataPointId !== 'string') {
    throw new Error(`The provided externalId was not a string. Id: ${id}`);
  }

  return dataPointId;
};

export const highlightMapIcon = ({
  map,
  highlightLayer,
  value,
  layer,
}: {
  map: Map;
  highlightLayer: keyof typeof HIGHLIGHT_LAYERS;
  value: string | number;
  layer: Layer & LayerWithToggleableGroup;
}): void => {
  if (!map) {
    return;
  }

  const { filterProperty, highlightLayers, getIconSizes } = HIGHLIGHT_LAYERS[highlightLayer];
  const { normalIconSize, enlargedIconSize } = getIconSizes(layer.id);

  // enlarge icon in source map layer
  if (map.getLayer(layer.id)) {
    map.setLayoutProperty(layer.id, 'icon-size', [
      'match',
      ['get', filterProperty],
      value,
      enlargedIconSize,
      normalIconSize,
    ]);
  }

  // add highlight in highlight layer
  highlightLayers.forEach(({ id }) => {
    if (map.getLayer(id)) {
      map.setFilter(id, ['==', filterProperty, value]);
    }
  });
};
